import { apiRequest } from '../client';
import { getApiToken } from '../authToken';
import { toJsonSafe } from '../json';

export function asCompanyUpdate(row) {
  if (!row) return null;
  const timestamp = row.timestamp ? new Date(row.timestamp) : new Date();
  return {
    ...row,
    timestamp: Number.isNaN(timestamp.getTime()) ? new Date() : timestamp,
    read: Boolean(row.read),
    type: typeof row.type === 'string' ? row.type.toLowerCase() : 'unknown',
  };
}

export function getUpdateServiceName(appointment) {
  if (!appointment) return '';

  if (appointment.serviceTags && appointment.serviceTags.length > 0) {
    const firstTag = appointment.serviceTags[0];
    if (typeof firstTag === 'string' && firstTag.trim()) {
      return firstTag.trim();
    }
    if (typeof firstTag === 'object' && typeof firstTag.name === 'string' && firstTag.name.trim()) {
      return firstTag.name.trim();
    }
  }

  if (appointment.serviceName && typeof appointment.serviceName === 'string' && appointment.serviceName.trim()) {
    return appointment.serviceName.trim();
  }

  if (appointment.service) {
    if (typeof appointment.service === 'string' && appointment.service.trim()) {
      return appointment.service.trim();
    }
    if (
      typeof appointment.service === 'object' &&
      typeof appointment.service.name === 'string' &&
      appointment.service.name.trim()
    ) {
      return appointment.service.name.trim();
    }
  }

  return '';
}

export async function listCompanyUpdates({ companyId, limit = 50, token } = {}) {
  const authToken = token || (await getApiToken());
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const query = params.toString();
  const path = `/api/companies/${encodeURIComponent(companyId)}/updates${query ? `?${query}` : ''}`;
  const result = await apiRequest(path, { token: authToken });
  return (result.updates || []).map(asCompanyUpdate);
}

export async function createCompanyUpdate({ companyId, update, token } = {}) {
  const authToken = token || (await getApiToken());
  const result = await apiRequest(`/api/companies/${encodeURIComponent(companyId)}/updates`, {
    token: authToken,
    method: 'POST',
    body: { update: toJsonSafe(update) },
  });
  return asCompanyUpdate(result.update);
}

export async function markCompanyUpdateRead({ companyId, id, token } = {}) {
  const authToken = token || (await getApiToken());
  const result = await apiRequest(
    `/api/companies/${encodeURIComponent(companyId)}/updates/${encodeURIComponent(id)}`,
    {
      token: authToken,
      method: 'PATCH',
      body: { read: true, readAt: new Date().toISOString() },
    }
  );
  return asCompanyUpdate(result.update);
}

export async function markCompanyUpdatesRead({ companyId, ids, token } = {}) {
  const authToken = token || (await getApiToken());
  return apiRequest(`/api/companies/${encodeURIComponent(companyId)}/updates/mark-read`, {
    token: authToken,
    method: 'POST',
    body: { ids: Array.isArray(ids) ? ids.filter(Boolean) : [] },
  });
}

export async function recordAppointmentUpdate({
  companyId,
  appointment,
  type,
  additionalDetails = {},
  token,
} = {}) {
  if (!companyId || !appointment) return null;

  const isClientAction = additionalDetails.updatedBy === 'client';
  const updateType = (isClientAction ? `client_${type}` : type).toLowerCase();

  return createCompanyUpdate({
    companyId,
    token,
    update: {
      type: updateType,
      appointmentId: appointment.id,
      timestamp: new Date().toISOString(),
      updatedBy: additionalDetails.updatedBy || 'system',
      updatedByName: additionalDetails.updatedByName || null,
      updatedByUid: additionalDetails.updatedByUid || null,
      lastAction: additionalDetails.lastAction || type,
      read: false,
      details: {
        clientName: appointment.clientName,
        dateTime: appointment.dateTime,
        service: additionalDetails.service || getUpdateServiceName(appointment),
        ...additionalDetails,
      },
    },
  });
}
